import { useSwitchContextMutation } from "Auth/Api/Mutations/Context/useSwitchContextMutation";
import type { SignInResult } from "Infrastructure/Api/Api";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  onBeforeReload?: () => void;
};

/**
 * Context switch is a major state transition (role, JWT, permission scope).
 * Use a hard navigation so that the page fully unmounts/reloads with the new
 * JWT, avoiding race conditions where:
 *  - the just-left role-restricted page (e.g. /merchants) refetches its
 *    queries with an incompatible JWT and 403s,
 *  - the AuthorizedRoute fires with stale Redux user.accessRights,
 *  - queryClient.clear() races with React unmount.
 *
 * window.location.replace gives us atomic transition: new JWT in localStorage
 * is read by app bootstrap, fresh user-info call seeds Redux with new role.
 * Same UX pattern as Slack/Discord workspace switching.
 */
export const useSwitchContextWithReload = ({ onBeforeReload }: Props = {}) => {
  const { mutate: switchContext, isPending: isSwitching } =
    useSwitchContextMutation({
      onSuccess: (result: SignInResult) => {
        setToken(result);
        onBeforeReload?.();
        window.location.replace(appUrl("homepage"));
      },
    });

  return { switchContext, isSwitching };
};
